/**
 * Faculty Dashboard
 * Interface for faculty members to create and manage course surveys
 * Similar to Teacher dashboard but with course-specific context
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { SurveyData } from '../App';
import { getCurrentUser } from '../auth/LoginButton';

export function FacultyDashboard() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [loading, setLoading] = useState(true);
  const currentUser = getCurrentUser();

  useEffect(() => {
    fetchMySurveys();
  }, []);

  const fetchMySurveys = async () => {
    try {
      // Filter surveys by current user's ID - Faculty only see their own surveys
      const response = await axios.get(`/surveys?creator_id=${currentUser.id}`);
      setSurveys(response.data);
      console.log(`🎓 [Faculty] Fetched ${response.data.length} surveys for user ${currentUser.email}`);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-primary)' }}>
        Loading your course surveys...
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--accent)', fontSize: '28px' }}>
            🎓 Course Surveys
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-secondary)' }}>
            Manage surveys for your courses
          </p>
        </div>
        <button
          onClick={() => navigate('/Surveys/create')}
          style={{
            padding: '12px 28px',
            background: 'var(--accent)',
            color: 'var(--button-text)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
            transition: 'all 0.3s'
          }}
        >
          + New Course Survey
        </button>
      </div>

      {surveys.length === 0 ? (
        <div style={{
          background: 'var(--card-bg)',
          border: '2px dashed var(--card-border)',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📋</div>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>No course surveys yet</h3>
          <p>Create surveys for your courses to gather student feedback</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {surveys.map((survey) => (
            <div
              key={survey.id}
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onClick={() => navigate(`/Surveys/edit/${survey.id}`)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
                    {survey.title}
                  </h3>
                  <p style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
                    {survey.description || 'No description'}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <span>📊 {survey.question_count} questions</span>
                    <span>
                      {survey.is_published ? (
                        <span style={{ color: 'var(--success)' }}>✓ Published</span>
                      ) : (
                        <span style={{ color: 'var(--warning)' }}>○ Draft</span>
                      )}
                    </span>
                    <span>📅 {new Date(survey.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/Surveys/responses/${survey.id}`);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'var(--button-bg)',
                      color: 'var(--button-text)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    View Responses
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
